import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Plus, ChevronDown, Bot, User, Loader2 } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

const SUGGESTIONS = [
  { key: 'portfolio', icon: '📊' },
  { key: 'bestAgent', icon: '🏆' },
  { key: 'rebalance', icon: '⚖️' },
  { key: 'risk', icon: '🛡️' },
  { key: 'explain', icon: '🔍' },
];

export default function AiChatWidget() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [pulse, setPulse] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Load latest session on first open
  useEffect(() => {
    if (open && messages.length === 0 && !sessionId) {
      api.get('/api/chat/history').then(res => {
        if (res.data.messages?.length > 0) {
          setMessages(res.data.messages);
          setSessionId(res.data.session_id);
        }
      }).catch(() => {});
    }
  }, [open, messages.length, sessionId]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setPulse(false);

    try {
      const res = await api.post('/api/chat/message', {
        message: text,
        session_id: sessionId,
      });
      const aiMsg = { role: 'assistant', content: res.data.response, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, aiMsg]);
      if (!sessionId && res.data.session_id) {
        setSessionId(res.data.session_id);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('chat.errorResponse'),
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const startNewSession = async () => {
    try {
      const res = await api.post('/api/chat/new-session');
      setSessionId(res.data.session_id);
      setMessages([]);
    } catch {}
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSuggestion = (key) => {
    const suggestions = {
      portfolio: t('chat.sugPortfolio'),
      bestAgent: t('chat.sugBestAgent'),
      rebalance: t('chat.sugRebalance'),
      risk: t('chat.sugRisk'),
      explain: t('chat.sugExplain'),
    };
    sendMessage(suggestions[key] || key);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-400 text-[#030508] flex items-center justify-center shadow-lg shadow-emerald-500/25 transition-all hover:scale-105 group"
          data-testid="ai-chat-toggle"
          style={{ borderRadius: '16px 16px 4px 16px' }}
        >
          <MessageCircle className="w-6 h-6" />
          {pulse && (
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full animate-pulse border-2 border-[#030508]" />
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[420px] h-[600px] bg-[#0A0D12] border border-white/10 shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
          style={{ borderRadius: '20px 20px 4px 20px' }}
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0D1117]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center" style={{ borderRadius: '10px' }}>
                <Bot className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-white tracking-wide">BlackOreX AI</div>
                <div className="text-[10px] text-emerald-400 font-mono flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  {t('chat.portfolioAware')}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={startNewSession}
                className="p-1.5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors rounded"
                title={t('chat.newChat')}
                data-testid="new-chat-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors rounded"
                data-testid="close-chat-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 scrollbar-thin">
            {messages.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-12 h-12 bg-emerald-500/10 flex items-center justify-center mb-4" style={{ borderRadius: '14px' }}>
                  <Bot className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-white font-bold text-sm mb-1">{t('chat.welcomeTitle')}</h3>
                <p className="text-slate-500 text-xs mb-6 leading-relaxed max-w-[280px]">
                  {t('chat.welcomeDesc')}
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTIONS.map(s => (
                    <button
                      key={s.key}
                      onClick={() => handleSuggestion(s.key)}
                      className="px-3 py-1.5 bg-white/5 border border-white/10 text-xs text-slate-300 hover:bg-emerald-500/10 hover:border-emerald-500/30 hover:text-emerald-400 transition-all"
                      style={{ borderRadius: '8px' }}
                      data-testid={`suggestion-${s.key}`}
                    >
                      <span className="mr-1">{s.icon}</span>
                      {t(`chat.sug_${s.key}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center mt-0.5 ${
                  msg.role === 'user' ? 'bg-blue-500/20' : 'bg-emerald-500/20'
                }`} style={{ borderRadius: '8px' }}>
                  {msg.role === 'user'
                    ? <User className="w-3 h-3 text-blue-400" />
                    : <Bot className="w-3 h-3 text-emerald-400" />
                  }
                </div>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-500/15 text-white border border-blue-500/20'
                    : 'bg-white/5 text-slate-200 border border-white/5'
                }`} style={{ borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px' }}>
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-invert prose-sm max-w-none [&_p]:mb-1.5 [&_ul]:mb-1.5 [&_li]:mb-0.5 [&_strong]:text-emerald-400 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2.5">
                <div className="w-6 h-6 flex-shrink-0 bg-emerald-500/20 flex items-center justify-center" style={{ borderRadius: '8px' }}>
                  <Bot className="w-3 h-3 text-emerald-400" />
                </div>
                <div className="bg-white/5 border border-white/5 px-4 py-3 flex items-center gap-2" style={{ borderRadius: '14px 14px 14px 4px' }}>
                  <Loader2 className="w-3.5 h-3.5 text-emerald-400 animate-spin" />
                  <span className="text-xs text-slate-500 font-mono">{t('chat.analyzing')}</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Scroll indicator */}
          {messages.length > 4 && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-[72px] left-1/2 -translate-x-1/2 bg-[#0D1117] border border-white/10 p-1 rounded-full opacity-60 hover:opacity-100 transition-opacity"
            >
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-3 py-3 border-t border-white/10 bg-[#0D1117]">
            <div className="flex items-center gap-2 bg-[#0A0D12] border border-white/10 px-3 py-2 focus-within:border-emerald-500/40 transition-colors" style={{ borderRadius: '12px' }}>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={t('chat.placeholder')}
                className="flex-1 bg-transparent text-white text-sm placeholder-slate-600 outline-none font-mono"
                disabled={loading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                className="p-1.5 bg-emerald-500 text-[#030508] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-emerald-400 transition-colors"
                style={{ borderRadius: '8px' }}
                data-testid="chat-send-btn"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-[9px] text-slate-600 mt-1.5 text-center font-mono">
              {t('chat.poweredBy')}
            </div>
          </form>
        </div>
      )}
    </>
  );
}
